<template>
  <article class="faq-column">
    <h2 id="mobile-app-faq-heading">Frequently asked questions about the mobile app</h2>
    <accordion header-id="mobile-app-faq-heading">
      <accordion-item
        title="How do I use my membership plan to start a charging session at an  Electrify America charger?"
      >
        <p>
          As an Electrify America Pass or Pass+ member, if you’ve downloaded the Electrify America app and added a valid
          payment method to your account, you have two ways to start a charge by smartphone:
        </p>
        <div class="flex">
          <div class="body">
            <p>
              1. Using the
              <router-link class="link" :to="{ name: 'glossary-en', query: { load: 9 }, hash: '#mobile-app' }"
                >digital membership pass</router-link
              >, which requires tapping or holding the smartphone near the charger’s RFID reader.
            </p>
            <ul>
              <li>
                First, plug the connector into the vehicle. Follow all steps on the charger screen before tapping or
                holding your phone near the reader.
              </li>
              <li>
                <b>Android users:</b> Ensure that NFC is turned on in your phone settings and that the smartphone screen
                is unlocked before tapping the smartphone to the reader—look for the symbol below. The Electrify America
                app does not need to be open.
              </li>
              <li>
                <b>iOS users:</b> Open Apple Wallet or the Electrify America app. Tap the smartphone to the reader on
                the right side of the charger—look for the symbol shown here
              </li>
            </ul>
            <p>2. Tap the "Start Charging" button from within the Electrify America app.</p>
            <ul>
              <li>
                Ensure that your smartphone’s location services are enabled, the Electrify America app is installed on
                your phone, and you are logged into your account.
              </li>
              <li>
                Open the app and from the map screen (or your Favorites), select the charging station where you are
                located. You must be near the charger to start a charge.
              </li>
              <li>
                Select the charger by referring to the Charger ID number at the top of the charger’s screen, and tap to
                start a charging session.
              </li>
            </ul>
          </div>
          <div class="images">
            <img
              loading="lazy"
              class="image"
              src="@/assets/images/Mobile-App/tap-to-pay.png"
              alt="Tap a smartphone to the charging station’s RFID reader."
            />
            <img
              loading="lazy"
              class="image"
              src="@/assets/images/Mobile-App/tap_to_start.png"
              alt="The mobile app’s “Tap to Start Charge” button."
            />
          </div>
        </div>
      </accordion-item>

      <accordion-item title="How do I pay for charging and for the Pass+ subscription fee?">
        <p>
          First, sign up for Electrify America’s Pass or Pass+ membership in the mobile app. After signing up, follow
          the prompts to add a debit or credit card to your account. All costs for charging, idling, and any applicable
          taxes will be charged to the payment method saved to your account at the end of your charging session.
        </p>
        <p>
          To ensure sufficient funds are available, a temporary authorization hold in $20 increments will be placed on
          the card on file. You will only be charged for the actual cost of charging. Hold release times vary by
          bank<reference-link :num="1">
            You can manage your plan anytime in the mobile app under “Payment & Plans.” For longer charging sessions,
            additional holds in $20 increments may be placed as needed. You are responsible for all applicable fees
            incurred during a charging session. </reference-link
          >.
        </p>
        <p>
          If you’re a Pass+ member, your monthly subscription fee will be charged to your credit or debit card when you
          sign up and deducted monthly thereafter.
        </p>
        <p>Any remaining account balance will automatically be applied to your charging session.</p>
      </accordion-item>

      <accordion-item title="What happens if my Electrify America payment is invalid?">
        <p>
          If your payment method declines during a charging session, please update it to continue charging your vehicle.
          If the payment method is invalid, charging will not proceed.
        </p>
        <p>
          You can
          <a class="link" href="https://www.youtube.com/watch?v=AqKKD66TFJ0" target="_blank">update your credit card</a>
          in the Electrify America app under <strong>Account>Payments>Add a Payment Method</strong>.
        </p>
      </accordion-item>

      <accordion-item title="Can I use the Electrify America app with CarPlay and Android Auto?">
        <p>
          Yes! Please ensure your vehicle is compatible with CarPlay or Android Auto and your phone is up to date. Here
          are a few ways you can use the Electrify America app in vehicles compatible with CarPlay and Android Auto:
        </p>
        <ul>
          <li>Navigate to charging stations while on the go</li>
          <li>View charger details</li>
          <li>Manage your charging session</li>
        </ul>
      </accordion-item>
      <accordion-item
        title="What’s the latest version of software needed to use the Electrify America app with CarPlay and Android Auto?"
      >
        <p>The Electrify America app is supported on both iOS 16+ and Android 9+.</p>
      </accordion-item>
    </accordion>
  </article>
</template>

<script>
import Accordion from './Accordion/Accordion.vue';
import AccordionItem from './Accordion/AccordionItem.vue';
import ReferenceLink from './ReferenceLink/ReferenceLink.vue';

export default {
  name: 'faqs',
  components: {
    Accordion,
    AccordionItem,
    ReferenceLink,
  },
  computed: {
    isMobile() {
      return this.$resize && this.$mq.below(1130);
    },
  },
};
</script>

<style scoped lang="scss">
.flex {
  flex-direction: column;
  @media (min-width: 750px) {
    flex-direction: row;
  }

  .body {
    flex: 1;
  }

  .images {
    margin-left: 50px;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-evenly;
    align-items: center;
    @media (min-width: 750px) {
      flex-direction: column;
    }
    .image {
      width: 240px;
    }

    .image:nth-child(1) {
      @media (max-width: 750px) {
        margin-bottom: 30px;
      }
    }
  }
}
</style>
